import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { LinkPage, handleLeaveLink } from '../components/ButtonExitLink';
import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import {
  OTHER_REASON,
  PROVIDER_REASON,
  SKIP_FEEDBACK_REASONS,
  SkipReasonValue,
  buildSkipFeedbackPayload,
  isValidEmail,
  submitSkipFeedback,
} from '../utils/skipFeedback';

export default function MissingELD() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Use new centralized hooks
  const jwt = useJWT();
  const { serviceName, redirectURIError } = useAppState();

  // Check if we can redirect back to service
  const canRedirectToService = jwt.canRedirectToService;

  const [selectedReason, setSelectedReason] = useState<SkipReasonValue | ''>(
    PROVIDER_REASON
  );
  const [details, setDetails] = useState('');
  const [contactRequested, setContactRequested] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fontFamily =
    (theme.typography as any)?.fontFamily || 'Inter, sans-serif';

  const inputStyles = useMemo(
    () => ({
      width: '100%',
      mb: 2,
      '& .MuiOutlinedInput-root': {
        minHeight: '56px',
        borderRadius: '4px',
        fontFamily,
        '& fieldset': {
          borderColor: theme.palette?.grey?.[300] || '#C7CACB',
        },
        '&:hover fieldset': {
          borderColor: theme.palette?.text?.primary || '#263238',
        },
        '&.Mui-focused fieldset': {
          borderColor:
            theme.custom?.inputField?.focus?.borderColor ||
            (theme.palette?.primary as any)?.main ||
            '#B60207',
          borderWidth: theme.custom?.inputField?.focus?.borderWidth || '2px',
        },
        '&.MuiInputBase-multiline': {
          minHeight: '96px',
          '& textarea': {
            padding: '12px 0',
          },
        },
      },
      '& .MuiInputLabel-root': {
        fontFamily,
      },
    }),
    [
      fontFamily,
      theme.custom?.inputField?.focus?.borderColor,
      theme.custom?.inputField?.focus?.borderWidth,
      theme.palette?.grey,
      theme.palette?.primary,
      theme.palette?.text?.primary,
    ]
  );

  const handleReasonChange = (event: SelectChangeEvent<unknown>) => {
    const nextValue = event.target.value as SkipReasonValue | '';
    setSelectedReason(nextValue);
    if (nextValue !== PROVIDER_REASON && nextValue !== OTHER_REASON) {
      setDetails('');
    }
  };

  const reasonLabel = useMemo(() => {
    return SKIP_FEEDBACK_REASONS.find(
      (reason) => reason.value === selectedReason
    )?.label;
  }, [selectedReason]);

  // Handle returning to the service
  const handleReturn = async () => {
    if (isSubmitting) {
      return;
    }

    if (contactRequested) {
      const trimmedEmail = contactEmail.trim();
      if (trimmedEmail && !isValidEmail(trimmedEmail)) {
        setContactEmailError('Enter a valid email address.');
        return;
      }
    }

    setContactEmailError('');
    setIsSubmitting(true);
    amplitude.track('User Confirmed Skip Onboarding', {
      serviceName: serviceName,
      redirectURL: redirectURIError,
    });

    // Use the analytics system to track TSP_NOT_FOUND event and handle redirect
    try {
      const { payload: feedbackPayload, meta } = buildSkipFeedbackPayload({
        reasonValue: selectedReason,
        reasonLabel,
        details,
        contactRequested,
        contactName,
        contactEmail,
        defaultMessage: 'User reported they could not find their provider.',
      });

      const skipReasonParam = meta.reasonLabel?.trim()
        ? meta.reasonLabel?.trim()
        : meta.reasonValue || undefined;
      const skipTspParam = feedbackPayload.tsp_name?.trim() || undefined;

      // Create analytics context from JWT
      const analyticsContext = createAnalyticsContext(jwt, serviceName, {
        navigate: (path: string) => navigate(path),
        skipReasonParam,
        skipTspParam,
      });

      const tokenForFeedback = analyticsContext.token || jwt.rawToken || null;
      await submitSkipFeedback(tokenForFeedback, feedbackPayload);

      // Use the confirmed skip function which handles both webhook and redirect
      await Analytics.confirmedSkip.tspNotFound(analyticsContext);
    } catch (error) {
      // Fallback to direct redirect if analytics fails
      console.warn(
        '[MissingELD] Analytics failed, falling back to direct redirect:',
        error
      );
      handleLeaveLink({
        token: jwt.rawToken || '',
        redirectURIError: redirectURIError || '/error',
        serviceName,
        pageOnExit: LinkPage.Approve,
        skipReasonParam: reasonLabel?.trim()
          ? reasonLabel?.trim()
          : selectedReason || undefined,
        skipTspParam:
          selectedReason === PROVIDER_REASON
            ? details?.trim() || undefined
            : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle going back to the previous page
  const handleBack = () => {
    // Use browser history to go back to the previous page
    // This handles both cases - coming from Approve or SelectTSP
    navigate(-1);
  };

  // Extract theme values safely
  const primaryColor = (theme.palette?.primary?.main as string) || '#B60207';
  const textSecondaryColor =
    (theme.palette?.text?.secondary as string) || '#586166';
  const paperBgColor =
    (theme.palette?.background?.paper as string) || '#FFFFFF';
  const accentIconColor = '#5C656A'; // Using consistent accent color for the back button
  const textPrimaryColor =
    (theme.palette?.text?.primary as string) || '#263238';

  const placeholderTypography = (
    <Typography component='span' color='text.secondary'>
      Select a reason (optional)
    </Typography>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        minHeight: '100vh',
        py: { xs: 0, sm: 0 },
        px: { xs: 0, sm: 2 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: { xs: '100%', sm: '532px' },
          minWidth: { xs: 'calc(100vw - 16px)', sm: '532px' },
          maxWidth: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
          borderRadius: '4px',
          position: 'relative',
          mb: { xs: 4, sm: 2 },
          mx: { xs: 1, sm: 'auto' },
          bgcolor: paperBgColor,
        }}
      >
        {/* Back button */}
        <IconButton
          onClick={handleBack}
          sx={{
            position: 'absolute',
            left: { xs: '8px', sm: '12px' },
            top: { xs: '12px', sm: '16px' },
            width: { xs: '44px', sm: '50px' },
            height: { xs: '44px', sm: '50px' },
            color: accentIconColor,
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Main content area */}
        <Box
          sx={{
            padding: { xs: '40px 16px 24px', sm: '60px 50px 24px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: paperBgColor,
            borderRadius: '8px',
            minWidth: { xs: 'calc(100vw - 48px)', sm: 'auto' },
          }}
        >
          <Typography
            variant='h5'
            align='center'
            sx={{
              mb: 2,
              fontSize: { xs: '22px', sm: '28px' },
              lineHeight: { xs: '28px', sm: '36px' },
              fontWeight: 600,
              maxWidth: '432px',
              wordWrap: 'break-word',
              hyphens: 'auto',
            }}
          >
            Can't find your ELD provider?
          </Typography>

          <Typography
            variant='subtitle1'
            align='center'
            sx={{
              mb: 2,
              fontSize: { xs: '18px', sm: '20px' },
              lineHeight: { xs: '24px', sm: '28px' },
              fontWeight: 600,
              maxWidth: '468px',
              wordWrap: 'break-word',
            }}
          >
            Hang tight—more ELD providers are on the way
          </Typography>

          <Typography
            variant='body1'
            align='center'
            sx={{
              mb: 3,
              fontSize: { xs: '14px', sm: '16px' },
              lineHeight: { xs: '20px', sm: '24px' },
              maxWidth: '432px',
              color: textSecondaryColor,
            }}
          >
            Axle is continuously expanding its list of supported providers. New
            integrations are regularly.
          </Typography>

          <Box
            sx={{
              width: '100%',
              maxWidth: '432px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              mb: 2,
            }}
          >
            <TextField
              select
              fullWidth
              value={selectedReason}
              label='Reason (optional)'
              variant='outlined'
              sx={inputStyles}
              InputLabelProps={{
                shrink: true,
                sx: { fontFamily },
              }}
              SelectProps={{
                displayEmpty: true,
                onChange: handleReasonChange,
                renderValue: (value) => {
                  if (!value) {
                    return placeholderTypography;
                  }
                  const reason = SKIP_FEEDBACK_REASONS.find(
                    (item) => item.value === value
                  );
                  return reason?.label ?? String(value);
                },
              }}
            >
              <MenuItem value=''>
                <Typography variant='body2' color='text.secondary'>
                  No reason
                </Typography>
              </MenuItem>
              {SKIP_FEEDBACK_REASONS.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>

            {(selectedReason === PROVIDER_REASON ||
              selectedReason === OTHER_REASON) && (
              <TextField
                fullWidth
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                label={
                  selectedReason === PROVIDER_REASON
                    ? 'Which provider were you looking for?'
                    : 'Tell us more'
                }
                placeholder={
                  selectedReason === PROVIDER_REASON
                    ? 'Provider name'
                    : 'Optional details'
                }
                variant='outlined'
                sx={inputStyles}
                multiline={selectedReason === OTHER_REASON}
                minRows={selectedReason === OTHER_REASON ? 2 : undefined}
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={contactRequested}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setContactRequested(checked);
                    if (!checked) {
                      setContactName('');
                      setContactEmail('');
                      setContactEmailError('');
                    }
                  }}
                  sx={{
                    p: 0,
                    mr: 1.5,
                    color: textSecondaryColor,
                    '&.Mui-checked': {
                      color: (theme.palette?.primary as any)?.main || '#B60207',
                    },
                  }}
                />
              }
              label='Contact me'
              componentsProps={{
                typography: {
                  sx: {
                    fontFamily,
                    color: textPrimaryColor,
                  },
                },
              }}
              sx={{
                alignItems: 'center',
                width: '100%',
                mx: 0,
                mb: contactRequested ? 1 : 0,
              }}
            />

            {contactRequested && (
              <>
                <TextField
                  fullWidth
                  value={contactName}
                  onChange={(event) => setContactName(event.target.value)}
                  label='Name'
                  variant='outlined'
                  sx={inputStyles}
                />
                <TextField
                  fullWidth
                  value={contactEmail}
                  onChange={(event) => {
                    const value = event.target.value;
                    setContactEmail(value);
                    if (contactEmailError) {
                      if (!value.trim() || isValidEmail(value)) {
                        setContactEmailError('');
                      }
                    }
                  }}
                  label='Email'
                  type='email'
                  variant='outlined'
                  sx={inputStyles}
                  error={!!contactEmailError}
                  helperText={contactEmailError}
                />
              </>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: '432px',
              padding: { xs: '0px 0px 16px', sm: '0px 0px 16px' },
              mb: 3,
            }}
          >
            {canRedirectToService ? (
              <Button
                variant='contained'
                onClick={handleReturn}
                disabled={isSubmitting}
                sx={{
                  height: { xs: '48px', sm: '40px' },
                  width: '100%',
                  bgcolor: primaryColor,
                  color:
                    theme.palette.getContrastText?.(primaryColor) || '#FFFFFF',
                  borderRadius: '4px',
                  fontSize: { xs: '16px', sm: '14px' },
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.75px',
                }}
              >
                RETURN TO {serviceName?.toUpperCase() || 'SERVICE'}
              </Button>
            ) : (
              <Typography
                variant='body2'
                sx={{
                  textAlign: 'center',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  mt: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                You can safely close this tab or window.
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
